import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = typeof __SITE_URL__ !== 'undefined' ? __SITE_URL__ : 'https://itaobuyspread.bond';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      headline: 'Tutte le Categorie di Itaobuy Spreadsheet: La Lista Completa Aggiornata 2026',
      description: 'Scopri tutte le 14 categorie disponibili su Itaobuy Spreadsheet. Dalle scarpe ai profumi, ogni categoria è organizzata per aiutarti a trovare esattamente i prodotti che cerchi.',
      image: 'https://readdy.ai/api/search-image?query=Colorful%20organized%20product%20catalog%20spread%20across%20a%20large%20wooden%20table%20with%20labeled%20sections%20for%20shoes%20clothing%20bags%20accessories%20and%20perfumes%2C%20bright%20natural%20daylight%20from%20large%20windows%2C%20creative%20studio%20workspace%20aesthetic%2C%20rainbow%20of%20fashion%20items%20neatly%20arranged%2C%20organized%20chaos%2C%20editorial%20catalog%20photography&width=1200&height=600&seq=article-categorie-hero&orientation=landscape',
      datePublished: '2026-07-14',
      dateModified: '2026-07-14',
      author: { '@type': 'Organization', name: 'Itaobuy Spreadsheet', url: siteUrl },
      publisher: { '@type': 'Organization', name: 'Itaobuy Spreadsheet', url: siteUrl },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categorie Prodotti', item: `${siteUrl}/articoli/itaobuy-spreadsheet-categorie-prodotti` },
      ],
    },
  ],
};

export default function ArticleCategorie() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd as unknown as Record<string, unknown>} />
      <Navbar />

      <main>
        <div className="pt-24 md:pt-28 pb-6 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-xs"></i>
              Torna alla Home
            </button>
          </div>
        </div>

        <article className="w-full pb-16 md:pb-20 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              Categorie
            </span>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
              Tutte le Categorie di Itaobuy Spreadsheet: La Lista Completa Aggiornata 2026
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
              Scopri tutte le 14 categorie disponibili su Itaobuy Spreadsheet. Dalle scarpe ai profumi, ogni categoria è organizzata per aiutarti a trovare esattamente i prodotti che cerchi in modo rapido ed efficiente.
            </p>

            <div className="h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Colorful%20organized%20product%20catalog%20spread%20across%20a%20large%20wooden%20table%20with%20labeled%20sections%20for%20shoes%20clothing%20bags%20accessories%20and%20perfumes%2C%20bright%20natural%20daylight%20from%20large%20windows%2C%20creative%20studio%20workspace%20aesthetic%2C%20rainbow%20of%20fashion%20items%20neatly%20arranged%2C%20organized%20chaos%2C%20editorial%20catalog%20photography&width=1200&height=600&seq=article-categorie-hero&orientation=landscape"
                alt="Categorie Itaobuy Spreadsheet"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Panoramica delle Categorie Popolari di Itaobuy Spreadsheet
                </h2>
                <p className="mb-4">
                  <strong className="text-foreground-950">Itaobuy Spreadsheet</strong> offre un sistema di categorie estremamente organizzato che rende la ricerca prodotti un'esperienza fluida e piacevole. Le categorie più popolari come Scarpe, Felpe e T-Shirt attirano la maggior parte degli utenti italiani grazie alla loro completezza e agli aggiornamenti frequenti.
                </p>
                <p className="mb-4">
                  La categoria <strong className="text-foreground-950">Scarpe (Shoes)</strong> è senza dubbio la regina dello spreadsheet. Con centinaia di modelli tra sneakers, scarpe eleganti, stivali e sandali, questa sezione è un paradiso per gli amanti delle calzature. Ogni prodotto è accompagnato da informazioni dettagliate che ti aiutano a fare la scelta giusta.
                </p>
                <p className="mb-4">
                  Le <strong className="text-foreground-950">Felpe e Maglioni (Hoodies/Sweaters)</strong> rappresentano un'altra categoria di punta. Perfette per la stagione autunno-inverno, queste sezioni offrono capi di tendenza con materiali di qualità. La varietà di stili, colori e taglie disponibili rende questa categoria una delle più visitate.
                </p>
                <p>
                  Le <strong className="text-foreground-950">T-Shirt</strong> completano il trio delle categorie più popolari. Dai modelli basic alle grafiche più elaborate, questa sezione offre opzioni per ogni gusto e occasione. È una delle categorie con il miglior rapporto qualità-prezzo su tutto Itaobuy Spreadsheet.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Categorie Secondarie: Giacche, Pantaloni e Accessori
                </h2>
                <p className="mb-4">
                  Oltre alle categorie principali, <strong className="text-foreground-950">Itaobuy Spreadsheet</strong> include numerose categorie secondarie altrettanto interessanti. Le <strong className="text-foreground-950">Giacche (Jackets)</strong> offrono una selezione curata di capispalla per ogni stagione, dai leggeri bomber primaverili ai pesanti parka invernali.
                </p>
                <p className="mb-4">
                  La categoria <strong className="text-foreground-950">Pantaloni e Corti (Pants/Shorts)</strong> è perfetta per completare il tuo guardaroba. Con modelli che spaziano dai jeans casual ai pantaloni eleganti, troverai sicuramente ciò che fa per te. I filtri ti aiutano a navigare rapidamente tra le diverse opzioni.
                </p>
                <p>
                  Gli <strong className="text-foreground-950">Accessori (Accessories)</strong> sono la ciliegina sulla torta. Orologi, bracciali, collane e molto altro: questa categoria ti permette di aggiungere quel tocco finale che fa la differenza nel tuo stile personale.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Categorie Speciali: Borse, Profumi e Altro
                </h2>
                <p className="mb-4">
                  Per chi cerca qualcosa di più specifico, <strong className="text-foreground-950">Itaobuy Spreadsheet</strong> mette a disposizione categorie specializzate. Le <strong className="text-foreground-950">Borse (Bags)</strong> includono zaini, borse a mano, tracolle e pochette di ogni stile e dimensione. Una categoria perfetta per chi ama gli accessori funzionali.
                </p>
                <p className="mb-4">
                  I <strong className="text-foreground-950">Profumi (Perfumes)</strong> rappresentano una delle categorie più esclusive. Con fragranze che spaziano dai classici intramontabili alle novità più ricercate, questa sezione soddisferà anche i nasi più esigenti.
                </p>
                <p>
                  Infine, la categoria <strong className="text-foreground-950">Altro (Other Stuff)</strong> raccoglie tutti quei prodotti che non rientrano nelle categorie standard ma che meritano comunque la tua attenzione. È qui che spesso si nascondono le vere sorprese.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Come Sfruttare al Meglio le Categorie di Itaobuy Spreadsheet
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                      <i className="ri-filter-line text-sm"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">Filtra per Popolarità</h3>
                    <p className="text-sm text-foreground-500">Inizia sempre dalle categorie più popolari per scoprire i prodotti che vanno per la maggiore tra gli utenti italiani.</p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                      <i className="ri-compass-line text-sm"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">Esplora Ogni Sezione</h3>
                    <p className="text-sm text-foreground-500">Non limitarti alle prime categorie: esplora ogni angolo dello spreadsheet per trovare gemme nascoste.</p>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl bg-gradient-to-br from-secondary-700 to-secondary-900 p-8 md:p-10 text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                  Esplora Tutte le Categorie Ora
                </h2>
                <p className="text-background-50/70 mb-6 max-w-lg mx-auto text-sm">
                  Visita il sito principale per navigare tra tutte le categorie e trovare i prodotti perfetti per te.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center px-6 py-3 rounded-full bg-background-50 text-foreground-900 text-sm font-semibold whitespace-nowrap transition-all hover:bg-background-100 cursor-pointer"
                  >
                    itaobuy sheet
                    <i className="ri-arrow-right-up-line ml-2 text-xs"></i>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap transition-all hover:bg-accent-600 cursor-pointer"
                  >
                    buy it now
                    <i className="ri-arrow-right-up-line ml-2 text-xs"></i>
                  </a>
                </div>
              </section>

              <div className="pt-6 border-t border-background-200/70">
                <p className="text-sm text-foreground-500 mb-3">
                  Questo articolo fa parte della guida completa su Itaobuy Spreadsheet. Per ulteriori informazioni:
                </p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-home-line text-xs"></i>
                    Torna alla Home
                  </button>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-200 transition-colors whitespace-nowrap">
                    <i className="ri-external-link-line text-xs"></i>
                    Visita Kaspreadsheet
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}